import { useCallback, useState } from 'react';
import { api, API_BASE_URL } from '@/lib/apiClient';

/**
 * Derive the admin panel URL from the same API base the app is using, so a
 * token minted against localhost is exchanged against localhost and a token
 * minted against Railway dev is exchanged against admindev.flock.church.
 * Mirrors flock-expo/src/hooks/useOpenAdminPanel.ts (getAdminUrl).
 */
function getAdminUrl(): string {
  const override = import.meta.env.VITE_ADMIN_URL as string | undefined;
  if (override) return override;

  const base = API_BASE_URL;
  if (base.includes('localhost') || base.includes('127.0.0.1')) {
    return 'http://localhost:3000';
  }
  if (base.includes('app.flock.church')) {
    return 'https://admin.flock.church';
  }
  return 'https://admindev.flock.church';
}

const ADMIN_URL = getAdminUrl();

/**
 * Generate a one-time login token and open the admin panel with
 * auto-authentication in a new tab. Falls back to the plain URL on failure.
 */
export function useOpenAdminPanel() {
  const [opening, setOpening] = useState(false);

  const openAdminPanel = useCallback(async () => {
    setOpening(true);
    try {
      const result = await api.post<{ success: boolean; token: string }>(
        '/auth/admin-login-token'
      );
      if (result?.token) {
        window.open(`${ADMIN_URL}/auth/token-login?token=${result.token}`, '_blank');
      } else {
        window.open(ADMIN_URL, '_blank');
      }
    } catch {
      window.open(ADMIN_URL, '_blank');
    } finally {
      setOpening(false);
    }
  }, []);

  return { openAdminPanel, opening, adminUrl: ADMIN_URL };
}

export default useOpenAdminPanel;
